import { useCorrectMatch } from '../../api/review';
import type { ReviewMatch } from '../../api/review';

function money(match: ReviewMatch): string {
  if (match.promo_price) return `$${match.promo_price} (was $${match.regular_price ?? '—'})`;
  return match.regular_price ? `$${match.regular_price}` : 'no price';
}

/** One candidate product, with the evidence needed to judge it.
 *
 * A match is a claim about which jar on which shelf, and the name alone is a
 * poor way to check it: "Duke's Real Mayonnaise, 30 oz Jar" and its 18 oz
 * squeeze bottle read almost identically. The photograph and the link to the
 * store's own page are what make the claim checkable.
 */
export function MatchRow({
  match,
  ingredient,
  newStore,
}: {
  match: ReviewMatch;
  ingredient: string;
  newStore: boolean;
}) {
  const correct = useCorrectMatch();

  return (
    <li
      className={[
        'review-match',
        match.chosen ? 'is-chosen' : 'is-alternate',
        match.pinned ? 'is-pinned' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="review-store">{newStore ? match.store : ''}</span>

      {match.image_url ? (
        <img
          className="review-thumb"
          src={match.image_url}
          alt=""
          loading="lazy"
          width={44}
          height={44}
        />
      ) : (
        <span className="review-thumb review-thumb--empty" aria-hidden="true" />
      )}

      <span className="review-product">
        {match.product_url ? (
          <a href={match.product_url} target="_blank" rel="noreferrer">
            {match.product_name}
          </a>
        ) : (
          match.product_name
        )}
        {match.size && <em> · {match.size}</em>}
        {/* Why a surprising match is not necessarily wrong: it was found by a
            broader word than the ingredient's own name. */}
        {match.matched_as && match.matched_as !== ingredient && (
          <em className="review-matched-as"> found as “{match.matched_as}”</em>
        )}
      </span>

      <span className="review-price">{money(match)}</span>
      <span className="review-confidence" title="how sure the matcher was">
        {Math.round(match.confidence * 100)}%
      </span>
      <span className="review-verdict">
        <button
          type="button"
          className="ghost"
          disabled={correct.isPending || match.pinned}
          onClick={() => correct.mutate({ id: match.id, accepted: true })}
          title={
            match.chosen
              ? `Confirm this is the ${ingredient} to buy`
              : `Buy this instead for ${ingredient}`
          }
        >
          {match.pinned ? 'Confirmed' : match.chosen ? 'Yes' : 'Use this'}
        </button>
        <button
          type="button"
          className="ghost danger"
          disabled={correct.isPending}
          onClick={() => correct.mutate({ id: match.id, accepted: false })}
          title={`Not ${ingredient} — never match these again`}
        >
          Not this
        </button>
      </span>
    </li>
  );
}

export function Pager({
  page,
  total,
  pageSize,
  hasNext,
  onPage,
  noun,
}: {
  page: number;
  total: number;
  pageSize: number;
  hasNext: boolean;
  onPage: (next: number) => void;
  noun: string;
}) {
  if (total <= pageSize) return null;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav className="review-pages" aria-label="Review pages">
      <button
        type="button"
        className="btn btn--small"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >
        ← Previous
      </button>
      <span className="muted small">
        {total.toLocaleString()} {noun} · page {page} of {lastPage.toLocaleString()}
      </span>
      <button
        type="button"
        className="btn btn--small"
        disabled={!hasNext}
        onClick={() => onPage(page + 1)}
      >
        Next →
      </button>
    </nav>
  );
}
